import type { NoteName } from '@tunasong/schemas'
import type { NoteDegree } from '../note/index.js'
import { getNoteByChromaticIndex, getNoteChromaticIndex, isNoteAugmented, isNoteMinor } from '../note/index.js'
import { getNote } from '../note/note-util.js'
import type { ChordElement, ChordType, ChordVariant } from './chord-types.js'
import { ALL_CHORD_TYPES } from './chord-types.js'
import { noteSignature as signature } from './util.js'

export class ChordBuilder {
  elementNames: ChordElement[] = []
  constructor(public root: NoteName) {}

  static create = (root: NoteName) => new ChordBuilder(root)

  getName() {
    return `${this.root}${this.elementNames.join('')}`
  }

  getAliasNames() {
    // Aliases for the root triad when we don't have any additional elements
    const isRootOnly = this.elementNames.length === 1
    const elementAliases: Record<string, string[] | undefined> = {}
    for (const element of this.elementNames) {
      const spec = ALL_CHORD_TYPES[element]
      elementAliases[element] = spec?.aliases
    }
    // We now have a map of aliases for each element type
    // So if e.g., the root '' has one alias ['M'], and maj7 has aliases ['CΔ', 'CM7'],

    // Stems are elements that we are incrementally building
    const elementCount = this.elementNames.length
    const aliases: string[] = []
    let stems: string[] = []

    for (const [idx, element] of this.elementNames.entries()) {
      const elementAliases = ALL_CHORD_TYPES[element]?.aliases
      if (!elementAliases) {
        stems = stems.map(s => `${s}${element}`)
        continue
      }
      if (idx === 0) {
        stems.push(this.root)
      }
      // If we have a root triad, we can add the aliases for the root. Otherwise we don't use the root aliases
      if (idx === 0 && !isRootOnly) {
        continue
      }

      for (const alias of elementAliases) {
        const updatedStems = stems.map(s => `${s}${alias}`)
        if (elementCount === idx + 1) {
          aliases.push(...updatedStems)
        } else {
          stems.push(...updatedStems)
        }
      }
    }
    return aliases
  }

  /** Normalized note signature - i.e., a string of all the notes in the Chord */
  getNoteSignature() {
    return signature(this.getNotes())
  }

  getChord(): ChordVariant {
    return { root: this.root, variant: this.elementNames.join('') as ChordType }
  }

  getNotes(): NoteName[] {
    return this.elementNames
      .map(element => {
        const spec = ALL_CHORD_TYPES[element]
        if (!spec) {
          throw new Error(`Spec ${element} does not exist!`)
        }
        /** @todo Must use bias here for the map. We should probably use chromatic indices instead to avoid problems with different names for the same note */
        return spec.intervals.map(n => {
          const note = getNote(this.root, n as NoteDegree)

          const noteIsAugmentedOrMinor = isNoteAugmented(note) || isNoteMinor(note)
          const rootIsAugmentedOrMinor = isNoteAugmented(this.root) || isNoteMinor(this.root)
          const type = this.root.endsWith('b') ? 'minor' : 'augmented'
          const alternativeNote = getNoteByChromaticIndex(getNoteChromaticIndex(note), type)
          return rootIsAugmentedOrMinor && noteIsAugmentedOrMinor && alternativeNote ? alternativeNote : note
        })
      })
      .flat()
  }

  /** Add an element, If none is given, start with a major triad */
  add(element: ChordElement = '') {
    this.elementNames.push(element)
    return this
  }
}
